import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import LoginModal from '../components/LoginModal';
import * as api from '../services/api';
import AddressForm from './AddressForm';
import './Checkout.css';

export default function Checkout() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { cart, clearCart } = useCart();
  const cartItems = cart?.items || [];
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }
    if (!cartItems || cartItems.length === 0) {
      navigate('/cart');
      return;
    }
    loadAddresses();
  }, [isAuthenticated, navigate, cartItems]);

  // Close login modal if user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && showLoginModal) {
      setShowLoginModal(false);
      loadAddresses();
    }
  }, [isAuthenticated, showLoginModal]);

  const loadAddresses = async () => {
    try {
      const userData = await api.getUserProfile();
      const userAddresses = userData.addresses || [];
      setAddresses(userAddresses);
      if (userAddresses.length > 0 && !selectedAddress) {
        setSelectedAddress(userAddresses[0]);
      }
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const handleAddressSelect = (address) => {
    setSelectedAddress(address);
  };

  const handleAddressSubmit = async (formData) => {
    try {
      await api.addAddress(formData);
      await loadAddresses();
      setShowAddressForm(false);
    } catch (error) {
      console.error('Error saving address:', error);
      alert('Failed to save address. Please try again.');
    }
  };

  const calculateTotals = () => {
    const subtotal = cartItems.reduce((sum, item) => {
      const price = parseFloat(item.variant?.price || item.product?.price || item.price || 0);
      const quantity = parseInt(item.quantity || 1);
      const itemTotal = (isNaN(price) ? 0 : price) * (isNaN(quantity) ? 1 : quantity);
      return sum + (isNaN(itemTotal) ? 0 : itemTotal);
    }, 0);
    const handlingCharge = 5; // Fixed handling charge
    const deliveryCharge = subtotal < 100 ? 20 : 0; // ₹20 if cart value < ₹100, else free
    const shipping = deliveryCharge + handlingCharge; // Total shipping includes both charges
    const total = (isNaN(subtotal) ? 0 : subtotal) + shipping;
    return { 
      subtotal: isNaN(subtotal) ? 0 : subtotal,
      handlingCharge,
      deliveryCharge,
      shipping, 
      total: isNaN(total) ? 0 : total 
    };
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      alert('Please select or add a delivery address');
      return;
    }

    if (placingOrder) return;

    setPlacingOrder(true);
    try {
      const { subtotal, deliveryCharge, handlingCharge, shipping, total } = calculateTotals();
      
      const orderItems = cartItems
        .filter(item => item) // Filter out any null/undefined items
        .map(item => {
          // Get price from nested structure
          const rawPrice = item.variant?.price || item.product?.price || item.price || 0;
          const price = parseFloat(rawPrice);
          const quantity = parseInt(item.quantity || 1, 10);
          
          // Ensure we have valid numbers
          const validPrice = (isNaN(price) || price <= 0) ? 0 : price;
          const validQty = (isNaN(quantity) || quantity <= 0) ? 1 : quantity;
          
          return {
            product: item.product?._id || item.productId || item._id,
            title: item.product?.title || item.product?.name || item.title || item.name || 'Product',
            sku: item.variant?.sku || item.sku,
            price: validPrice,
            qty: validQty,
            size: item.variant?.sizeLabel || item.variant?.label || item.size
          };
        })
        .filter(item => item.price > 0 && item.qty > 0); // Filter out invalid items

      const deliveryAddress = {
        name: selectedAddress.name || selectedAddress.fullName,
        phone: selectedAddress.phone,
        street: selectedAddress.street || selectedAddress.line1,
        floor: selectedAddress.floor,
        area: selectedAddress.area,
        city: selectedAddress.city,
        state: selectedAddress.state,
        pincode: selectedAddress.pincode || selectedAddress.postalCode,
        landmark: selectedAddress.landmark,
        type: selectedAddress.type || selectedAddress.label
      };

      // Validate that we have valid items
      if (!orderItems || orderItems.length === 0) {
        alert('No valid items in cart. Please add items to cart.');
        setPlacingOrder(false);
        return;
      }

      // Debug: Log order items to check for NaN
      console.log('Order items:', orderItems);
      orderItems.forEach((item, idx) => {
        if (isNaN(item.price) || isNaN(item.qty)) {
          console.error(`Invalid item at index ${idx}:`, item);
        }
      });

      // Note: Backend recalculates subtotal/total from items, so we don't need to send them
      // But we'll send them anyway for consistency
      // Send shipping as deliveryCharge + handlingCharge
      const orderData = {
        items: orderItems,
        shipping: shipping || 0, // This includes both deliveryCharge and handlingCharge
        deliveryAddress: deliveryAddress,
        paymentMethod: 'cod',
        notes: notes.trim() || undefined
      };

      console.log('Order data being sent:', orderData);
      const order = await api.placeOrder(orderData);
      
      // Clear cart after successful order
      clearCart();
      
      // Navigate to order confirmation page
      navigate(`/orders/${order._id}`);
    } catch (error) {
      console.error('Error placing order:', error);
      alert(error.response?.data?.message || 'Failed to place order. Please try again.');
    } finally {
      setPlacingOrder(false);
    }
  };

  const { subtotal, handlingCharge, deliveryCharge, shipping, total } = calculateTotals();
  
  // Ensure totals are valid numbers for display
  const displaySubtotal = isNaN(subtotal) ? 0 : subtotal;
  const displayHandlingCharge = isNaN(handlingCharge) ? 0 : handlingCharge;
  const displayDeliveryCharge = isNaN(deliveryCharge) ? 0 : deliveryCharge;
  const displayShipping = isNaN(shipping) ? 0 : shipping;
  const displayTotal = isNaN(total) ? 0 : total;

  if (!cartItems || cartItems.length === 0) {
    return null;
  }

  return (
    <div className="checkout-container">
      <div className="checkout-header">
        <button onClick={() => navigate('/cart')} className="back-button">
          ← Back to Cart
        </button>
        <h1>Checkout</h1>
      </div>

      <div className="checkout-content">
        <div className="checkout-left">
          {/* Delivery Address Section */}
          <div className="checkout-section">
            <h2>Delivery Address</h2>
            {addresses.length === 0 ? (
              <div className="no-address">
                <p>No saved addresses. Please add an address to continue.</p>
                <button 
                  className="btn primary"
                  onClick={() => setShowAddressForm(true)}
                >
                  + Add Address
                </button>
              </div>
            ) : (
              <>
                <div className="addresses-list">
                  {addresses.map((address) => (
                    <div
                      key={address._id}
                      className={`address-option ${selectedAddress?._id === address._id ? 'selected' : ''}`}
                      onClick={() => handleAddressSelect(address)}
                    >
                      <div className="address-radio">
                        <input
                          type="radio"
                          name="address"
                          checked={selectedAddress?._id === address._id}
                          onChange={() => handleAddressSelect(address)}
                        />
                      </div>
                      <div className="address-details">
                        <div className="address-header">
                          <strong>{address.name || address.fullName}</strong>
                          <span className="address-type">{address.type || address.label || 'Home'}</span>
                        </div>
                        {address.phone && (
                          <div className="address-phone">📞 {address.phone}</div>
                        )}
                        <div className="address-text">
                          {address.street || address.line1}
                          {address.floor && `, Floor ${address.floor}`}
                          {address.landmark && `, ${address.landmark}`}
                          <br />
                          {address.area && `${address.area}, `}
                          {address.city}, {address.state} - {address.pincode || address.postalCode}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  className="btn-secondary add-address-btn"
                  onClick={() => setShowAddressForm(true)}
                >
                  + Add New Address
                </button>
              </>
            )}
          </div>

          {/* Payment Method Section */}
          <div className="checkout-section">
            <h2>Payment Method</h2>
            <div className="payment-method">
              <div className="payment-option selected">
                <div className="payment-radio">
                  <input type="radio" name="payment" checked readOnly />
                </div>
                <div className="payment-details">
                  <div className="payment-name">
                    <span className="payment-icon">💵</span>
                    Cash on Delivery (COD)
                  </div>
                  <div className="payment-description">
                    Pay when you receive your order
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Order Notes Section */}
          <div className="checkout-section">
            <h2>Order Notes (Optional)</h2>
            <textarea
              className="order-notes"
              placeholder="Any special instructions for delivery..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="3"
            />
          </div>
        </div>

        {/* Order Summary */}
        <div className="checkout-right">
          <div className="order-summary-card">
            <h2>Order Summary</h2>
            
            <div className="order-items-summary">
              {cartItems.map((item, index) => (
                <div key={index} className="summary-item">
                  <div className="item-info">
                    <div className="item-name">
                      {item.product?.title || item.product?.name || item.title || item.name || 'Product'}
                    </div>
                    {(item.variant?.sizeLabel || item.variant?.label || item.size) && (
                      <div className="item-variant">
                        Size: {item.variant?.sizeLabel || item.variant?.label || item.size}
                      </div>
                    )}
                    <div className="item-quantity">Qty: {item.quantity || 1}</div>
                  </div>
                  <div className="item-price">₹{((item.variant?.price || item.product?.price || item.price || 0) * (item.quantity || 1)).toFixed(2)}</div>
                </div>
              ))}
            </div>

            <div className="order-totals">
              <div className="total-row">
                <span>Subtotal</span>
                <span>₹{displaySubtotal.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Delivery charge</span>
                <span>
                  {displayDeliveryCharge === 0 ? (
                    <>
                      <span style={{textDecoration: 'line-through', color: '#999', marginRight: '0.5rem'}}>₹20</span>
                      <span style={{color: '#27ae60', fontWeight: '600'}}>FREE</span>
                    </>
                  ) : (
                    `₹${displayDeliveryCharge.toFixed(2)}`
                  )}
                </span>
              </div>
              <div className="total-row">
                <span>Handling charge</span>
                <span>₹{displayHandlingCharge.toFixed(2)}</span>
              </div>
              <div className="total-row final-total">
                <span>Total</span>
                <span>₹{displayTotal.toFixed(2)}</span>
              </div>
            </div>

            <button
              className="btn primary place-order-btn"
              onClick={handlePlaceOrder}
              disabled={!selectedAddress || placingOrder}
            >
              {placingOrder ? 'Placing Order...' : `Place Order (₹${displayTotal.toFixed(2)})`}
            </button>

            {!selectedAddress && (
              <p className="address-warning">Please select a delivery address</p>
            )}
          </div>
        </div>
      </div>

      {showAddressForm && (
        <AddressForm
          isOpen={showAddressForm}
          onClose={() => setShowAddressForm(false)}
          onSubmit={handleAddressSubmit}
          userPhone={user?.phone}
        />
      )}

      {showLoginModal && (
        <LoginModal 
          onClose={() => {
            setShowLoginModal(false);
            // If still not authenticated, redirect to cart
            if (!isAuthenticated) {
              navigate('/cart');
            }
          }} 
        />
      )}
    </div>
  );
}

